/**
 * Hit feedback: the wiring between "what the simulation computed" and "what
 * the pilot perceives".
 *
 * The 2026-08-14 request's core diagnosis was: every number needed to tell a
 * rifle graze (285) from a blade direct hit (2,887) already existed, and all
 * of it was discarded at the single call `effects.impact(position)`. This
 * module owns the classification half of the fix: pure functions, no three.js,
 * so the thresholds that decide what a hit *feels* like are unit-testable and
 * live in one place instead of being sprinkled through Game.ts.
 */

/** Which system produced the hit. `system` = test/scripted damage: no per-hit
 * theatrics, only outcome-driven ones (stagger break, kill), so screenshots of
 * scripted mission steps don't show absurd floating numbers. */
export type HitWeapon = 'rifle' | 'missile' | 'blade' | 'system'

/**
 * Severity band of a single applied hit, derived from `damageApplied` (the
 * post-multiplier figure the simulation already computes and previously threw
 * away). Bands are chosen so every weapon/direct combination lands in a
 * distinct-feeling place:
 *
 *   rifle 285 → graze | rifle direct 470 → solid | missile 380 → solid
 *   missile direct 627 → solid | blade 1,750 → heavy | blade direct 2,887 → severe
 */
export type HitTier = 'graze' | 'solid' | 'heavy' | 'severe'

export interface HitEvent {
  weapon: HitWeapon
  tier: HitTier
  /** True when the ACS-overload multiplier (×1.65) was applied. */
  direct: boolean
  damageApplied: number
}

export function hitTier(damageApplied: number): HitTier {
  if (damageApplied >= 2_000) return 'severe'
  if (damageApplied >= 900) return 'heavy'
  if (damageApplied >= 340) return 'solid'
  return 'graze'
}

/**
 * Simulation freeze on connection, in seconds. Zero for the rifle on purpose:
 * at 7 rounds/second any stop would turn sustained fire into slideshow, and
 * the rifle's read is carried by sparks, numbers and sound instead. The blade
 * is the weapon whose weight the whole request hangs on, so it gets the
 * longest weapon stop; a stagger break outranks every weapon.
 */
export function weaponHitStopSeconds(weapon: HitWeapon, direct: boolean): number {
  switch (weapon) {
    case 'blade':
      return direct ? 0.12 : 0.085
    case 'missile':
      return direct ? 0.055 : 0.03
    default:
      return 0
  }
}

/** Stop for the moment an enemy's ACS breaks. Longest stop in the game. */
export const STAGGER_BREAK_STOP_SECONDS = 0.14

/** Stop for a killing blow, by enemy weight class. */
export function killHitStopSeconds(heavy: boolean): number {
  return heavy ? 0.11 : 0.07
}

/**
 * Camera shake for incoming damage. Replaces the old fixed 0.48, which meant a
 * 357-damage graze and a 944-damage direct hit shook the frame identically —
 * the pilot had no somatic read on how bad a hit was. Monotonic in damage,
 * clamped so worst-case fire cannot make the camera unusable.
 *
 *   skirmisher 357 → 0.58 | bulwark 546 → 0.75 | escalated 651 → 0.85
 *   escalated direct 944 → 1.05 (cap)
 */
export function incomingShake(damageApplied: number): number {
  const safe = Number.isFinite(damageApplied) ? Math.max(0, damageApplied) : 0
  return Math.min(1.05, 0.26 + (safe / 1_000) * 0.9)
}

/** HUD damage-flash intensity for incoming damage; same idea, HUD-side scale. */
export function incomingPulse(damageApplied: number): number {
  const safe = Number.isFinite(damageApplied) ? Math.max(0, damageApplied) : 0
  return Math.min(1, 0.32 + safe / 1_200)
}

/**
 * FOV punch (degrees subtracted, i.e. a brief tighten) for outgoing moments.
 * The camera physically answering a landed blade is half of what makes it feel
 * heavier than a rifle round in a muted recording.
 */
export function fovKickDegrees(event: { weapon: HitWeapon; direct: boolean }): number {
  if (event.weapon === 'blade') return event.direct ? 4.6 : 3.4
  if (event.weapon === 'missile' && event.direct) return 1.6
  return 0
}

/**
 * Cumulative damage read-out policy for the HUD: rapid rifle fire keeps one
 * counter per target alive and growing instead of confetti of tiny numbers.
 * A gap longer than the window starts a new counter.
 */
export const DAMAGE_COUNTER_WINDOW_SECONDS = 0.6

export interface DamageCounterState {
  total: number
  tier: HitTier
  direct: boolean
  lastHitAt: number
}

/**
 * Fold a hit into a per-target damage counter. Pure so the windowing rule is
 * testable: within the window totals accumulate and the display tier is the
 * tier of the running total (a stream of grazes that adds up to heavy damage
 * *should* escalate the read-out); past the window the counter restarts.
 */
export function accumulateDamage(
  previous: DamageCounterState | null,
  damageApplied: number,
  direct: boolean,
  now: number,
  windowSeconds: number = DAMAGE_COUNTER_WINDOW_SECONDS,
): DamageCounterState {
  const fresh = !previous || now - previous.lastHitAt > windowSeconds
  const total = fresh ? damageApplied : previous.total + damageApplied
  return {
    total,
    tier: hitTier(total),
    direct: fresh ? direct : previous.direct || direct,
    lastHitAt: now,
  }
}
